import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Button from '../components/common/Button';
import ParticleCanvas from '../components/common/ParticleCanvas';
import { User, Mail, Lock, Gift, ShieldCheck, AlertCircle, Key, ArrowLeft, CheckCircle2 } from 'lucide-react';

const Register = ({ onSwitchToLogin, onGoToHome }) => {
  const { sendSignupOTP, register, authError, setAuthError } = useAuth();
  
  // Registration Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [referralCodeInput, setReferralCodeInput] = useState('');
  const [otp, setOtp] = useState('');
  
  // UI Flow State
  const [step, setStep] = useState(1); // 1: Details, 2: OTP Verification
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [infoMsg, setInfoMsg] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) {
      setReferralCodeInput(ref.toUpperCase());
    }
  }, []);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) return;
    if (password.length < 6) {
      setAuthError('Password must be at least 6 characters.');
      return;
    }
    if (!referralCodeInput || !referralCodeInput.trim()) {
      setAuthError('A valid referral code is required to register an account.');
      return;
    }

    setIsSubmitting(true);
    setInfoMsg('');
    setAuthError('');

    const result = await sendSignupOTP(email, referralCodeInput.trim().toUpperCase());
    setIsSubmitting(false);

    if (result.success) {
      setStep(2);
      setInfoMsg(result.message || 'Verification OTP code sent to your email address.');
    }
  };

  const handleVerifyAndRegister = async (e) => {
    e.preventDefault();
    if (!otp) return;
    setIsSubmitting(true);
    setAuthError('');

    await register(name, email, password, referralCodeInput, otp);
    setIsSubmitting(false);
  };

  return (
    <div className="relative min-h-screen bg-[#09090B] flex items-center justify-center p-4 font-sans overflow-hidden">
      {/* Interactive Infinite Canvas Background */}
      <ParticleCanvas />

      <div className="relative z-10 w-full max-w-md bg-[#121215]/90 backdrop-blur-xl border border-[#27272A] rounded-3xl p-8 shadow-card">
        <div className="text-center mb-8">
          {/* <img 
            src="/logo.png" 
            alt="DUBAI GOLD X Logo" 
            className="w-16 h-16 object-contain mx-auto mb-3 drop-shadow-[0_0_25px_rgba(245,158,11,0.45)]" 
          /> */}
          <h2 className="text-2xl font-extrabold tracking-tight text-[#FFFFFF]">
            {step === 1 ? 'Create Account' : 'Verify Email OTP'}
          </h2>
          <p className="text-xs text-[#9CA3AF] mt-1">
            {step === 1 ? 'Join DUBAI GOLD X MT5 Trading Community' : `Verification code sent to ${email}`}
          </p>
        </div>

        {authError && (
          <div className="mb-6 p-4 bg-[#EF4444]/15 border border-[#EF4444]/40 rounded-2xl flex items-start space-x-3 text-xs text-[#EF4444] font-medium">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{authError}</span>
          </div>
        )}

        {infoMsg && (
          <div className="mb-6 p-4 bg-[#10B981]/15 border border-[#10B981]/40 rounded-2xl flex items-start space-x-3 text-xs text-[#10B981] font-medium">
            <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{infoMsg}</span>
          </div>
        )}

        {/* STEP 1: Registration Form */}
        {step === 1 && (
          <form onSubmit={handleSendOTP} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-[#9CA3AF] mb-1.5">Full Name</label>
              <div className="relative">
                <User className="w-4 h-4 text-[#9CA3AF] absolute left-3.5 top-3.5" />
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(e) => { setName(e.target.value); setAuthError(''); }}
                  placeholder="Enter your full name"
                  className="w-full bg-[#09090B] border border-[#27272A] text-[#FFFFFF] placeholder-[#9CA3AF]/40 rounded-xl pl-10 pr-4 py-3 text-xs focus:outline-none focus:border-[#F97316] focus:ring-1 focus:ring-[#F97316] transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#9CA3AF] mb-1.5">Email Address</label>
              <div className="relative">
                <Mail className="w-4 h-4 text-[#9CA3AF] absolute left-3.5 top-3.5" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setAuthError(''); }}
                  placeholder="user@dubaigoldx.com"
                  className="w-full bg-[#09090B] border border-[#27272A] text-[#FFFFFF] placeholder-[#9CA3AF]/40 rounded-xl pl-10 pr-4 py-3 text-xs focus:outline-none focus:border-[#F97316] focus:ring-1 focus:ring-[#F97316] transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#9CA3AF] mb-1.5">Password</label>
              <div className="relative">
                <Lock className="w-4 h-4 text-[#9CA3AF] absolute left-3.5 top-3.5" />
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setAuthError(''); }}
                  placeholder="At least 6 characters"
                  className="w-full bg-[#09090B] border border-[#27272A] text-[#FFFFFF] placeholder-[#9CA3AF]/40 rounded-xl pl-10 pr-4 py-3 text-xs focus:outline-none focus:border-[#F97316] focus:ring-1 focus:ring-[#F97316] transition-colors"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-semibold text-[#9CA3AF]">Referral Code</label>
                <span className="text-[10px] font-bold text-[#F97316] uppercase tracking-wider bg-[#F97316]/10 px-2 py-0.5 rounded border border-[#F97316]/30">
                  Required
                </span>
              </div>
              <div className="relative">
                <Gift className="w-4 h-4 text-[#F59E0B] absolute left-3.5 top-3.5" />
                <input
                  type="text"
                  required
                  value={referralCodeInput}
                  onChange={(e) => { setReferralCodeInput(e.target.value.toUpperCase()); setAuthError(''); }}
                  placeholder="Enter required referral code (e.g. REF123ABC)"
                  className="w-full bg-[#09090B] border border-[#27272A] text-[#FFFFFF] placeholder-[#9CA3AF]/40 rounded-xl pl-10 pr-4 py-3 text-xs focus:outline-none focus:border-[#F59E0B] focus:ring-1 focus:ring-[#F59E0B] transition-colors uppercase font-mono tracking-wider font-bold"
                />
              </div>
            </div>

            <Button type="submit" variant="primary" size="lg" className="w-full mt-2" disabled={isSubmitting}>
              {isSubmitting ? 'Sending Verification OTP...' : 'Send Verification OTP'}
            </Button>
          </form>
        )}

        {/* STEP 2: OTP Verification Screen */}
        {step === 2 && (
          <form onSubmit={handleVerifyAndRegister} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-[#9CA3AF] mb-1.5">6-Digit Verification Code (OTP)</label>
              <div className="relative">
                <Key className="w-4 h-4 text-[#F59E0B] absolute left-3.5 top-3.5" />
                <input
                  type="text"
                  required
                  maxLength={6}
                  value={otp}
                  onChange={(e) => { setOtp(e.target.value); setAuthError(''); }}
                  placeholder="123456"
                  className="w-full bg-[#09090B] border border-[#27272A] text-[#FFFFFF] placeholder-[#9CA3AF]/40 rounded-xl pl-10 pr-4 py-3 text-sm font-mono tracking-widest text-center font-bold focus:outline-none focus:border-[#F97316] focus:ring-1 focus:ring-[#F97316] transition-colors"
                />
              </div>
            </div>

            <Button type="submit" variant="primary" size="lg" className="w-full mt-2" disabled={isSubmitting}>
              {isSubmitting ? 'Verifying...' : 'Verify Email & Create Account'}
            </Button>

            <div className="flex items-center justify-between text-xs pt-2">
              <button
                type="button"
                onClick={() => { setStep(1); setAuthError(''); setInfoMsg(''); }}
                className="text-[#9CA3AF] hover:text-[#FFFFFF] flex items-center space-x-1"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                <span>Edit Details</span>
              </button>

              <button
                type="button"
                onClick={handleSendOTP}
                className="text-[#F97316] font-bold hover:underline"
              >
                Resend Code
              </button>
            </div>
          </form>
        )}

        <div className="mt-6 pt-6 border-t border-[#27272A] text-center space-y-2">
          <p className="text-xs text-[#9CA3AF]">
            Already have an account?{' '}
            <button
              onClick={onSwitchToLogin}
              className="text-[#F97316] font-bold hover:underline hover:text-[#FB923C]"
            >
              Sign In
            </button>
          </p>

          {onGoToHome && (
            <button
              onClick={onGoToHome}
              className="text-xs text-[#9CA3AF] hover:text-white font-semibold flex items-center justify-center space-x-1 mx-auto pt-1"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>Back to Home</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Register;
